import { memo, useState, useRef, forwardRef } from 'react';
import { Input, InputProps, cn } from '@page/ui';

interface CollapsibleInputProps extends Omit<InputProps, 'placeholder'> {
  placeholder: string;
  alwaysExpanded?: boolean;
  collapsedClassName?: string;
  expandedClassName?: string;
}

const CollapsibleInputBase = (
  {
    placeholder,
    alwaysExpanded = false,
    value = '',
    onChange,
    className,
    collapsedClassName = 'w-34',
    expandedClassName = 'w-64',
    ...rest
  }: CollapsibleInputProps,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      inputRef.current?.blur();

      return;
    }

    if (event.key === 'Escape') {
      inputRef.current?.blur();
    }
  };

  const isExpanded = isFocused || !!value || alwaysExpanded;

  return (
    <div
      ref={containerRef}
      className={cn('relative transition-all', isExpanded ? expandedClassName : collapsedClassName, className)}
    >
      <Input
        ref={(node) => {
          inputRef.current = node;

          if (!ref) return;

          if (typeof ref === 'function') return ref(node);

          ref.current = node;
        }}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        size="sm"
        className={cn(
          'font-body-sm font-semibold',
          isExpanded
            ? 'placeholder:text-text-tertiary'
            : 'placeholder:text-text-primary cursor-pointer hover:bg-bg-inter-secondary-hover',
        )}
        {...rest}
      />
    </div>
  );
};

export const CollapsibleInput = memo(forwardRef(CollapsibleInputBase));
